import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { API_ENDPOINTS } from "../../constants/apiConstants";
import { buildSessionTokenUrl, isSendbirdSessionExpired } from "../../helpers/sendbird";
import { iThunkApi } from "../../types/commonTypes";
import {
  iChangePasswordPayload,
  iChangePasswordResponse,
  iLoginPayload,
  iLoginResponse,
  iLogoutResponse,
} from "../../types/userType";
import type { ISendbirdSessionPayload } from "../../types/sendbirdTypes";
import { apiManager, isApiSuccess } from "../apiManager";
import { iApiSuccess } from "../../types/commonTypes";
import { DeviceTypeEnum } from "../../constants/enums";

const apiErrorPayload = (error: unknown) => {
  const body = (error as AxiosError)?.response?.data as
    | { statusCode?: number; message?: string }
    | undefined;
  return {
    statusCode: body?.statusCode ?? null,
    message: body?.message ?? "Something went wrong. Please try again.",
  };
};

export const loginUserThunk = createAsyncThunk<
  iLoginResponse,
  iLoginPayload,
  iThunkApi
>("user/loginUser", async (payload, thunkAPI) => {
  try {
    const resp = await apiManager(API_ENDPOINTS.LOGIN, { ...payload, deviceType: 1 }, "POST", {});
    if (!resp || !isApiSuccess(resp.status)) {
      return thunkAPI.rejectWithValue(resp.data);
    }
    return resp.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(apiErrorPayload(error));
  }
});

export const logoutUserThunk = createAsyncThunk<
  iLogoutResponse,
  any,
  iThunkApi
>("user/logoutUser", async (args, thunkApi) => {
  const deviceToken = thunkApi.getState().user.deviceToken;
  const resp = await apiManager(API_ENDPOINTS.LOGOUT, { deviceToken: deviceToken ?? '' }, "POST", {});
  if (!resp || !isApiSuccess(resp.status)) {
    return thunkApi.rejectWithValue(resp.data);
  }
  navigator?.clearAppBadge();
  return resp.data;
});

/** NAZ-604: Team member session from Baqaal (new-model channels). */
export const fetchTeamMemberSendbirdSessionThunk = createAsyncThunk<
  ISendbirdSessionPayload,
  { force?: boolean } | void,
  iThunkApi
>(
  "user/fetchTeamMemberSendbirdSession",
  async (_, thunkAPI) => {
    const resp = await apiManager(API_ENDPOINTS.SENDBIRD_SESSION_TOKEN, {}, "GET", {});
    if (!resp || !isApiSuccess(resp.status)) {
      return thunkAPI.rejectWithValue(resp.data);
    }
    const data = resp.data?.data as ISendbirdSessionPayload | undefined;
    if (!data?.accessToken || !data?.sendbirdId) {
      return thunkAPI.rejectWithValue(resp.data);
    }
    return data;
  },
  {
    condition: (args, { getState }) => {
      if (args?.force) return true;
      const { teamMemberSendbirdSession, isTeamMemberSendbirdTokenLoading } = getState().user;
      if (isTeamMemberSendbirdTokenLoading) return false;
      return !teamMemberSendbirdSession || isSendbirdSessionExpired(teamMemberSendbirdSession.expiresAt);
    },
  },
);

/** NAZ-604: Legacy channels — vendor owner token via optional sendbirdUserId query. */
export const fetchLegacyVendorOwnerSendbirdSessionThunk = createAsyncThunk<
  ISendbirdSessionPayload,
  { sendbirdUserId: string },
  iThunkApi
>("user/fetchLegacyVendorOwnerSendbirdSession", async ({ sendbirdUserId }, thunkAPI) => {
  const teamMemberSendbirdId = thunkAPI.getState().user.userDetails?.sendbirdId ?? "";
  const path = buildSessionTokenUrl(
    API_ENDPOINTS.SENDBIRD_SESSION_TOKEN,
    sendbirdUserId,
    teamMemberSendbirdId,
  );
  const resp = await apiManager(path, {}, "GET", {});
  if (!resp || !isApiSuccess(resp.status)) {
    return thunkAPI.rejectWithValue(resp.data);
  }
  const data = resp.data?.data as ISendbirdSessionPayload | undefined;
  if (!data?.accessToken || !data?.sendbirdId) {
    return thunkAPI.rejectWithValue(resp.data);
  }
  return data;
});

export const closeChanelThunk = createAsyncThunk<
  iApiSuccess,
  { channelUrl: string },
  iThunkApi
>("chat/closeChat", async (payload, thunkAPI) => {
  const resp = await apiManager(
    API_ENDPOINTS.CLOSE_CHAT + `/${payload.channelUrl}`,
    {},
    "PUT",
    {}
  );
  if (!resp || !isApiSuccess(resp.status)) {
    return thunkAPI.rejectWithValue(resp.data);
  }
  return resp.data;
});

export const saveDeviceTokenThunk = createAsyncThunk<
  iApiSuccess,
  { deviceToken: string, deviceType: DeviceTypeEnum },
  iThunkApi
>("user/saveDeviceToken", async (payload, thunkAPI) => {
  const resp = await apiManager(
    API_ENDPOINTS.SAVE_DEVICE_TOKEN,
    { deviceToken: payload.deviceToken, deviceType: payload.deviceType },
    "POST",
    {}
  );
  if (!resp || !isApiSuccess(resp.status)) {
    return thunkAPI.rejectWithValue(resp.data);
  }
  return resp.data;
});

export const forgotPasswordThunk = createAsyncThunk<
  iApiSuccess,
  { email: string },
  iThunkApi
>("user/forgotPassword", async (payload, thunkAPI) => {
  const resp = await apiManager(
    API_ENDPOINTS.FORGOT_PASSWORD(),
    { email: payload.email },
    "POST",
    {}
  );
  if (!resp || !isApiSuccess(resp.status)) {
    return thunkAPI.rejectWithValue(resp.data);
  }
  return resp.data;
});

export const resetPasswordThunk = createAsyncThunk<
  iApiSuccess,
  { token: string; password: string },
  iThunkApi
>("user/resetPassword", async (payload, thunkAPI) => {
  const resp = await apiManager(
    API_ENDPOINTS.RESET_PASSWORD(),
    { token: payload.token, password: payload.password },
    "PUT",
    {}
  );
  if (!resp || !isApiSuccess(resp.status)) {
    return thunkAPI.rejectWithValue(resp.data);
  }
  return resp.data;
});

/** A vendor replaces the temporary password from the Published email. */
export const changePasswordThunk = createAsyncThunk<
  iChangePasswordResponse,
  iChangePasswordPayload,
  iThunkApi
>("user/changePassword", async (payload, thunkAPI) => {
  try {
    const resp = await apiManager(API_ENDPOINTS.CHANGE_PASSWORD, payload, "PUT", {});
    if (!resp || !isApiSuccess(resp.status)) {
      return thunkAPI.rejectWithValue(resp.data);
    }
    return resp.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(apiErrorPayload(error));
  }
});
